

 "use client";

import { useEffect, useRef, useState } from "react";
import Navbar from "../components/Homapage/Navbar";
import { useTheme } from "@/app/context/ThemeContext";
import Footer from "../components/Homapage/Footer";
import { useLanguage } from "../components/context/LanguageContext";
import Breadcrumb from "../components/Breadcrumb";
import { STRAPI_URL } from "@/lib/strapi";
import Link from "next/link";

interface FeaturedImage {
  url: string;
  alternativeText?: string;
}

interface NewsItem {
  id: number;
  title: string;
  slug: string;
  summary: { type: string; children: { text: string }[] }[];
  featuredImage: FeaturedImage[];
  publishedAt: string;
  createdAt: string;
}

const translations: any = {
  fr: {
    Title: "Dernières Actualités",
    description1: "Restez informé avec les dernières mises à jour, annonces et perspectives de l'industrie d'ENAP.",
    description2: "News disponibles",
    label1: "Accueil",
    label2: "News",
    Loading: "Chargement ...",
  },
  en: {
    Title: "Latest News",
    description1: "Stay informed with the latest updates, announcements, and industry insights from ENAP.",
    description2: "Available News",
    label1: "Home",
    label2: "News",
    Loading: "Loading ...",
  },
  ar: {
    Title: "آخر الأخبار",
    description1: "ابقَ على اطلاع بأحدث التحديثات والإعلانات ورؤى صناعة ENAP.",
    description2: "الأخبار المتاحة",
    label1: "الرئيسية",
    label2: "الأخبار",
    Loading: "جارٍ تحميل  ...",
  },
};

export default function Newspage() {
  const { theme } = useTheme();
  const { language } = useLanguage();

  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const t = translations[language] || translations.en;
  const [loadingg, setLoadingg] = useState(false);
  const [logg, setLogg] = useState<string[]>([]);
  const hasRun = useRef(false); // LOCK

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    if (hasRun.current) return; // ⛔ stop duplicates
    hasRun.current = true;

    const processNews = async () => {
      setLoadingg(true);
      setLogg((l) => [...l, "⏳ Starting process..."]);

      try {
        const newsRes = await fetch(`${STRAPI_URL}/api/newsses?populate=*`);
        const newsData = await newsRes.json();

        const sentNews = newsData.data.filter((news: any) => news.sent === true);

        if (sentNews.length === 0) {
          setLogg((l) => [...l, "❌ No news with sent=true"]);
          return;
        }

        setLogg((l) => [...l, `�� ${sentNews.length} news to send`]);

        const subsRes = await fetch(`${STRAPI_URL}/api/subscribersses?populate=*`);
        const subsData = await subsRes.json();
        const subscribers = subsData.data;

        for (const news of sentNews) {
          const title = news.title || "Untitled";
          setLogg((l) => [...l, `➡️ Sending: ${title}`]);

          await fetch("/api/send-news", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ news, subscribers }),
          });

          await fetch("/api/update-news", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ documentId: news.documentId }),
          });

          setLogg((l) => [...l, `✅ Updated: ${title}`]);
        }

        setLogg((l) => [...l, "�� All news processed successfully"]);
      } catch (err) {
        console.error(err);
        setLogg((l) => [...l, "❌ Error occurred"]);
      } finally {
        setLoadingg(false);
      }
    };

    processNews();
  }, []);

  useEffect(() => {
    async function fetchNews(lang: string) {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${STRAPI_URL}/api/newsses?populate=*&locale=${lang}&sort=id:desc`,
          { cache: "no-store" }
        );
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

        const data = await res.json();
        setNews(data.data || []);
      } catch (err) {
        console.error("Error fetching news:", err);
        setError("Unable to load news. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchNews(language);
  }, [language]);

  // Calculate items to display
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentNews = news.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(news.length / itemsPerPage);

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  /* ================= DATE FORMAT ================= */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(
      language === "fr" ? "fr-FR" : language === "ar" ? "ar-DZ" : "en-US",
      { day: "numeric", month: "short", year: "numeric" }
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme === "dark" ? "bg-gray-900" : "bg-gray-50"}`}>
        <Navbar />
        <p className="text-center py-20 text-lg text-gray-500">{t.Loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${theme === "dark" ? "bg-gray-900 text-gray-100" : "bg-gray-50 text-gray-900"}`}>
        <Navbar />
        <div className="pt-24 text-center">
          <p className="text-xl mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-6 py-3 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-500">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${theme === "dark" ? "bg-gray-900 text-gray-100" : "bg-gray-50 text-gray-900"}`}>
      <section
        className={`pt-24 pb-20 relative overflow-hidden text-white ${
          theme === "dark"
            ? "bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800"
            : "bg-gradient-to-r from-blue-600 to-purple-700"
        }`}
        style={{
          backgroundImage: theme === "dark" ? undefined : "url('/test.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="relative z-30 max-w-7xl mx-auto px-4 pt-6 text-white">
          <Breadcrumb
            items={[
              { label: t.label1, href: "/" },
              { label: t.label2 },
            ]}
          />
        </div>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"></div>

        <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-lg">
            {t.Title}
          </h1>

          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed drop-shadow">
            {t.description1}
          </p>

          <div className="mt-12 flex flex-wrap justify-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold drop-shadow">{news.length}</div>
              <div className="text-sm font-medium text-blue-200">
                {t.description2}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 max-w-7xl mx-auto flex-1 w-full">
        {currentNews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {currentNews.map((item) => {
              const imageUrl = item.featuredImage[0]?.url
                ? `${STRAPI_URL}${item.featuredImage[0].url}`
                : "/video.png";

              return (
                <Link
                  key={item.id}
                  href={`/news/${item.slug}`}
                  className={`group flex flex-col shadow-md hover:shadow-lg transition-shadow duration-300 ${
                    theme === "dark" ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  {/* ---- Image (no border/rounded corners) ---- */}
                  <div
                    className={`relative w-full aspect-[16/9] overflow-hidden ${
                      theme === "dark" ? "bg-gray-950" : "bg-gray-100"
                    }`}
                  >
                    <img
                      src={imageUrl}
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* ---- Content (date + title) ---- */}
                  <div className="p-5 flex flex-col gap-2">
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {formatDate(item.publishedAt || item.createdAt)}
                    </span>
                    <span
                      className={`text-[17px] font-semibold leading-snug ${
                        theme === "dark" ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {item.title}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-xl font-semibold mb-2">Aucune nouveaux disponible</h3>
            <p>Revenez bientôt pour découvrir nos nouveaux contenus .</p>
          </div>
        )}

        {/* Pagination buttons */}
        {news.length > itemsPerPage && (
          <div className="flex justify-center items-center gap-4 mt-12">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className={`
                px-5 py-2.5 rounded-lg text-[14px] font-medium transition-colors
                ${currentPage === 1 ? "opacity-40 cursor-not-allowed" : ""}
                ${
                  theme === "dark"
                    ? "bg-gray-800 text-gray-200 hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
              `}
            >
              {language === "fr" && "Précédent"}
              {language === "en" && "Previous"}
              {language === "ar" && "السابق"}
            </button>

            <span className={`text-[13px] ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className={`
                px-5 py-2.5 rounded-lg text-[14px] font-medium transition-colors
                ${currentPage === totalPages ? "opacity-40 cursor-not-allowed" : ""}
                ${
                  theme === "dark"
                    ? "bg-white text-gray-900 hover:bg-gray-100"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }
              `}
            >
              {language === "fr" && "Suivant"}
              {language === "en" && "Next"}
              {language === "ar" && "التالي"}
            </button>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}